import React, {
  useState,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react';
import {
  Animated,
  ImageBackground,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl,
  ScrollView,
  View,
  ViewStyle,
  Dimensions,
  TouchableOpacity,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import responsiveStyles from './styles';
import {
  FontAwesomeIcon,
  FontAwesomeIconStyle,
} from '@fortawesome/react-native-fontawesome';
import {faAnglesUp} from '@fortawesome/pro-solid-svg-icons/faAnglesUp';
import {Components, Theme} from '@oneamerica/dxp-ui-components';
import {Images} from '../../../assets';
import Header from '../Header';
import Footer from '@oneamerica/dxp-ui-module-footer';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {
  AEM_API_BASE_URL,
  VINTAGE_PREFIX,
} from '../../config/getEnvironmentVars';
import { getLayoutType } from '../../../utils';

export type ScreenWrapperProps = {
  children?: ReactNode;
  paddingTop?: boolean;
  paddingBottom?: boolean;
  paddingHorizontal?: boolean;
  useMarginTop?: boolean;
  metrics?: boolean;
  scrollFooter?: boolean;
  ContentHeader?: () => ReactNode;
  ContentFooter?: () => ReactNode;
  onScroll?: (value: number) => void;
  onMomentumScrollEnd?: (
    event: NativeSyntheticEvent<NativeScrollEvent>
  ) => void;
  onRefresh?: () => void;
  style?: ViewStyle;
  scrollStyle?: ViewStyle;
  scrollEnabled?: boolean;
  contentStyle?: ViewStyle;
  stickyHeaderIndices?: number[];
  ignoreHeader?: boolean;
  ignoreFooter?: boolean;
  refreshing?: boolean;
  loader?: boolean;
  testID?: string;
  yOffset?: Animated.Value;
};

export type ScrollRef = ScrollView;

const SVGBackground = ({ children }) => {
  const styles = responsiveStyles();
  const layoutType = getLayoutType();
  const isWeb = Platform.OS === 'web';
  const backgroundImage =
    layoutType === 'desktop' ? Images.pageBG : Images.pageBG2;

  return (
    <ImageBackground
      source={backgroundImage}
      style={styles.wrapper}
      resizeMode="cover"
      imageStyle={
        isWeb
          ? { width: '100%', height: 2300, top: -35 }
          : { width: '100%', height: '100%' }
      }>
      {children}
    </ImageBackground>
  );
};
const getPaddingClasses = (paddingTop: boolean, paddingBottom: boolean) => {
  if (paddingTop && paddingBottom) return 'py-4 md:py-6';
  if (paddingTop) return 'pt-4 md:pt-6';
  if (paddingBottom) return 'pb-4 md:pb-6';
  return '';
};
const getContainerPaddingClass = (paddingHorizontal: boolean, layoutType: string) => {
  if (layoutType === 'desktop' && paddingHorizontal) return 'px-4 md:px-8';
  if (layoutType === 'tablet') return 'px-2';
  return '';
};
const getFooterPaddingBottom = (layoutType: string) => {
  if (layoutType === 'desktop') return 150;
  if (layoutType === 'tablet') return 100;
  return 50;
};

const ScreenWrapper = forwardRef<ScrollRef, ScreenWrapperProps>(
  (props: ScreenWrapperProps, ref): ReactElement | null => {
    const {
      paddingTop = true,
      paddingBottom = true,
      paddingHorizontal = true,
      useMarginTop = false,
      scrollFooter = false,
      stickyHeaderIndices,
      ignoreHeader = false,
      ignoreFooter = false,
      onScroll,
      onMomentumScrollEnd,
      onRefresh,
      refreshing = false,
      scrollStyle,
      scrollEnabled = true,
      contentStyle,
      loader = true,
      yOffset = new Animated.Value(0),
      children,
    } = props;

    const styles = responsiveStyles();
    const layoutType = getLayoutType();
    const {top, bottom} = useSafeAreaInsets();
    const headerHeight = 280;
    const [isScrollToTopVisible, setIsScrollToTopVisible] = useState(false);
    const {height} = Dimensions.get('window');
    const scrollViewRef = useRef<ScrollView | null>(null);
    useImperativeHandle(ref, () => scrollViewRef.current!, []);

    const displayHeaderHeight = ignoreHeader ? 0 : headerHeight;
    const footerPaddingBottom = getFooterPaddingBottom(layoutType);
    const paddingClasses = getPaddingClasses(paddingTop, paddingBottom);
    const containerPaddingClass = getContainerPaddingClass(paddingHorizontal, layoutType);

    const dynamicStyle = {
      marginTop: useMarginTop ? top : 0,
      paddingTop: useMarginTop ? 0 : top,
      paddingBottom: bottom + (useMarginTop ? top : 0),
    };
    const proxyOnRefresh = useCallback(() => {
      if (!refreshing) {
        onRefresh?.();
      }
    }, [onRefresh, refreshing]);

    yOffset.addListener(({value}) => {
      onScroll?.(value);
    });

    const handleScroll = (event) => {
      const offsetY = event.nativeEvent.contentOffset.y;
      setIsScrollToTopVisible(offsetY >= height * 0.25);
      Animated.event(
        [{ nativeEvent: { contentOffset: { y: yOffset } } }],
        { useNativeDriver: true }
      )(event);
    };

    const scrollToTop = () => {
      scrollViewRef?.current?.scrollTo({ offset: 0, animated: true });
    };

    const FOOTER_ENDPOINT =
    '/aemcontent/footer/master.model.json';

    return (
      <View
        className="flex-1"
        style={
          Platform.OS === 'web'
            ? ({ minHeight: '100vh' } as any)
            : { minHeight: height }
        }>
        <SVGBackground>
        <Header/>
          <Animated.ScrollView
            ref={scrollViewRef}
            keyboardDismissMode="interactive"
            keyboardShouldPersistTaps="handled"
            stickyHeaderIndices={stickyHeaderIndices}
            scrollEnabled={scrollEnabled}
            showsVerticalScrollIndicator={false}
            className="overflow-y-auto"
            style={[styles.scrollView, scrollStyle]}
            {...(Platform.OS === 'web' && { accessibilityRole: 'none' })}
            contentContainerStyle={[
              contentStyle,
              dynamicStyle,
              { paddingBottom: footerPaddingBottom },
            ]}
            onScroll={handleScroll}
            onMomentumScrollEnd={onMomentumScrollEnd}
            refreshControl={
              <RefreshControl
                testID="refresh-control"
                style={styles.proxyRefreshControl}
                refreshing={false}
                onRefresh={proxyOnRefresh}
                progressBackgroundColor={'transparent'}
              />
            }>
            <View
              className={`max-w-7xl w-full relative z-10 ${containerPaddingClass} ${paddingClasses} ${
                Platform.OS === 'ios' && '-mt-10'
              } ${Platform.OS === 'android' && '-mt-1'}`}>
              {children}
            </View>
            {scrollFooter && (
              <Footer
                footerAEMApiUrl={AEM_API_BASE_URL}
                footerAEMEndpoint={FOOTER_ENDPOINT}
                footerVintagePrefix={VINTAGE_PREFIX}
              />
            )}
          </Animated.ScrollView>

          {isScrollToTopVisible && (
            <View className="absolute bottom-0 right-0 mr-4 mb-4 md:mr-8 md:mb-8">
              <TouchableOpacity
                activeOpacity={0.8}
                style={styles.scrollToTop}
                accessibilityLabel="Scroll to Top"
                onPress={scrollToTop}>
                <View
                  style={{
                    backgroundColor: Theme.Color.ice4,
                    width: 48,
                    height: 48,
                    padding: 16,
                    borderRadius: 24,
                  }}>
                  <FontAwesomeIcon
                    style={styles.outlineNone as FontAwesomeIconStyle}
                    icon={faAnglesUp as IconProp}
                    size={16}
                    color={Theme.Color.oaBlue}
                  />
                </View>
              </TouchableOpacity>
            </View>
          )}

          {loader && (
            <View
              className="w-full h-[50px] justify-center absolute z-[1000]"
              style={{ top: top + displayHeaderHeight }}
              pointerEvents="none">
              <Components.LoadingOverlay
                loading={refreshing}
                testID="loading-overlay"
              />
            </View>
          )}
        </SVGBackground>
      </View>
    );
  }
);

export default ScreenWrapper;
